import { useState, useEffect, useRef, useMemo } from "react";
import './timer.css'
import { BsChevronUp } from "react-icons/bs";
import { BsChevronDown } from "react-icons/bs";
// import { customTheme as theme } from "../../../theme/customTheme";
import useCustomTheme from "../../../theme/useCustomTheme";

const Timer = (props) => {
  const listRef = useRef(null);
  const listInnerRef = useRef(null);
  const centerRef = useRef(null);
  const [theme ] = useCustomTheme()
  const { circularTime, finalTime, setFinalTime, numIntervals = 5, selectedColor, handleClose, finalDate, setFinalValue , extendEditingDate} = props;
  const [primaryColor, setPrimaryColor] = useState(`${theme.palette.primary.main}99`);
  const [primaryColorLight, setPrimaryColorLight] = useState(`${theme.palette.primary.main}70`);

  // selection state
  // start with no selection to avoid briefly showing 12:00 AM on mount; selection will be established by effect
  const [selectedTime, setSelectedTime] = useState(() => (circularTime.includes(finalTime) ? finalTime : null));
  const [currentIndex, setCurrentIndex] = useState(0);

  const DEFAULT_ITEM_HEIGHT = 40; // default px, can be changed via CSS variable --item-height
  const DEFAULT_VISIBLE_COUNT = 7;
  const [itemHeight, setItemHeight] = useState(DEFAULT_ITEM_HEIGHT);
  const [visibleCount, setVisibleCount] = useState(DEFAULT_VISIBLE_COUNT);

  useEffect(() => {
    const updateDimensions = () => {
      const el = listRef.current;
      if (!el) return;
      const timerEl = el.closest('.timer');
      let heightVal = null;
      let visibleVal = null;
      if (timerEl) {
        const cs = getComputedStyle(timerEl);
        heightVal = cs.getPropertyValue('--item-height');
        visibleVal = cs.getPropertyValue('--visible-count');
      }
      if (heightVal) {
        const px = parseInt(heightVal.trim().replace('px',''));
        if (!isNaN(px)) setItemHeight(px);
        else setItemHeight(DEFAULT_ITEM_HEIGHT);
      } else {
        setItemHeight(DEFAULT_ITEM_HEIGHT);
      }
      if (visibleVal) {
        const v = parseInt(visibleVal.trim());
        if (!isNaN(v) && v > 1) setVisibleCount(v);
        else setVisibleCount(DEFAULT_VISIBLE_COUNT);
      } else {
        setVisibleCount(DEFAULT_VISIBLE_COUNT);
      }
    }
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // build full list of times (keep all, but mark past ones as disabled)
  // parse time string like "2:30 PM" into a Date on selDateStart
  const parseTimeToDate = (selDateStart, timeStr) => {
    // match H:MM AM/PM
    const m = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (!m) {
      // fallback to Date.parse
      return new Date(Date.parse(`${selDateStart.toDateString()}, ${timeStr}`));
    }
    let hour = parseInt(m[1], 10);
    const minute = parseInt(m[2], 10);
    const ampm = m[3].toUpperCase();
    if (hour === 12 && ampm === 'AM') hour = 0;
    if (hour !== 12 && ampm === 'PM') hour += 12;
    const d = new Date(selDateStart);
    d.setHours(hour, minute, 0, 0);
    return d;
  };

  const timeItems = useMemo(() => {
    try {
      const now = new Date();
      const todayStart = new Date(now);
      todayStart.setHours(0,0,0,0);
      const selDateStart = new Date(finalDate.toDateString());
      selDateStart.setHours(0,0,0,0);

      if (selDateStart.getTime() === todayStart.getTime()) {
        // for current date, consider a slot "past" only after its end time (start + numIntervals minutes)
        // this allows a slot like 11:30 PM to remain selectable up until 12:00 AM when numIntervals=30
        return circularTime.map((time) => {
          const timeDate = parseTimeToDate(selDateStart, time);
          const slotEnd = new Date(timeDate.getTime() + numIntervals * 60 * 1000);
          const isPast = now.getTime() >= slotEnd.getTime();
          return { time, isPast };
        });
      } else if (selDateStart.getTime() < todayStart.getTime()) {
        // past date: mark all as past
        return circularTime.map((time) => ({ time, isPast: true }));
      } else {
        // future dates: all available
        return circularTime.map((time) => ({ time, isPast: false }));
      }
    } catch (e) {
      return circularTime.map(t => ({ time: t, isPast: false }));
    }
  }, [circularTime, finalDate]);

  const timeOptions = timeItems.map(i => i.time);

  // whenever timeItems or selectedTime changes, ensure currentIndex and selectedTime are consistent
  useEffect(() => {
    if (timeOptions.length === 0) {
      setCurrentIndex(0);
      // if no times at all, clear selection and parent
      setSelectedTime(null);
      setFinalTime(null);
      setFinalValue('');
      return;
    }

    const idx = timeOptions.indexOf(selectedTime);
    const selectedIdxIsPast = idx !== -1 && timeItems[idx] && timeItems[idx].isPast;
    if (idx !== -1 && !selectedIdxIsPast) {
      setCurrentIndex(idx);
      return;
    }

    // If no current good selection, pick earliest enabled (preferred behavior)
    const firstAvailable = timeItems.findIndex(i => !i.isPast);
    if (firstAvailable !== -1) {
      // Only set if different
      if (timeOptions[firstAvailable] !== selectedTime) {
        setValues(timeOptions[firstAvailable]);
      }
      setCurrentIndex(firstAvailable);
    } else {
      // all times are past for this date
      setSelectedTime(null);
      setFinalTime(null);
      setFinalValue('');
      setCurrentIndex(0);
    }
  }, [timeOptions, selectedTime, timeItems]);

  // Ensure on mount / date change we default to earliest enabled (covers race conditions)
  useEffect(() => {
    const firstAvailable = getFirstAvailableIndex();
    if (firstAvailable !== null) {
      const idx = timeOptions.indexOf(selectedTime);
      const selectedIdxIsPast = idx !== -1 && timeItems[idx] && timeItems[idx].isPast;
      if (idx === -1 || selectedIdxIsPast) {
        setValues(timeOptions[firstAvailable]);
        setCurrentIndex(firstAvailable);
      }
    }
  }, [finalDate, timeItems.join(','), circularTime.join(',')]);

  const setValues = (time) => {
    if (!time) return;
    setSelectedTime(time);
    let tempValue = new Date(Date.parse(`${finalDate.toDateString()}, ${time}`))
    setFinalTime(time);
    setFinalValue(tempValue.toLocaleString('en-US', {weekday: 'short', day: "2-digit", month: "2-digit", year: "numeric", hour: 'numeric', hour12: true, minute:'numeric'}))

    // sync currentIndex immediately to keep scroll/selection in sync
    const idx = timeOptions.indexOf(time);
    if (idx !== -1) {
      setCurrentIndex(idx);
      // ensure highlight recalculates after DOM update
      requestAnimationFrame(() => computeHighlight());
    }
  }

  const handleTimeSelection = (time) => {
    if (!time) return;

    setValues(time);
    let tempValue = new Date(Date.parse(`${finalDate.toDateString()}, ${time}`))
    setFinalValue(tempValue.toLocaleString('en-US', {weekday: 'short', day: "2-digit", month: "2-digit", year: "numeric", hour: 'numeric', hour12: true, minute:'numeric'}))

    if(extendEditingDate != true){
      handleClose(false)
    }
  };

  // wheel handling: make it less sensitive and clamp to ends; snap to nearest enabled
  const wheelAccumulatorRef = useRef(0);
  const lastWheelTimeRef = useRef(0);
  const WHEEL_THRESHOLD = 80; // accumulate this much deltaY before moving
  const WHEEL_COOLDOWN_MS = 100; // minimal time between moves (ms)

  const findNearestEnabledFrom = (startIndex, direction) => {
    if (!timeItems || timeItems.length === 0) return null;
    let i = startIndex;
    // move in direction until a non-past item is found
    while (i >= 0 && i < timeItems.length && timeItems[i].isPast) {
      i += direction;
    }
    // clamp to edges
    if (i < 0) return 0;
    if (i >= timeItems.length) return timeItems.length - 1;
    return i;
  }

  const getFirstAvailableIndex = () => {
    if (!timeItems || timeItems.length === 0) return null;
    const idx = timeItems.findIndex(i => !i.isPast);
    return idx === -1 ? null : idx;
  }

  const handleScroll = (event) => {
    if (event && typeof event.preventDefault === 'function') event.preventDefault();

    wheelAccumulatorRef.current += event.deltaY;
    const now = Date.now();

    // ensure we have available times
    if (getFirstAvailableIndex() === null) return;

    if (Math.abs(wheelAccumulatorRef.current) >= WHEEL_THRESHOLD && (now - lastWheelTimeRef.current) > WHEEL_COOLDOWN_MS && timeOptions.length > 0) {
      const direction = Math.sign(wheelAccumulatorRef.current);
      const steps = Math.max(1, Math.floor(Math.abs(wheelAccumulatorRef.current) / WHEEL_THRESHOLD));
      const currentIdx = timeOptions.indexOf(selectedTime) === -1 ? 0 : timeOptions.indexOf(selectedTime);
      let nextIndex = currentIdx + direction * steps;
      // clamp to bounds (no circular wrap)
      if (nextIndex < 0) nextIndex = 0;
      if (nextIndex > timeOptions.length - 1) nextIndex = timeOptions.length - 1;

      // if the target is disabled, find nearest enabled in the same direction
      if (timeItems[nextIndex] && timeItems[nextIndex].isPast) {
        const alt = findNearestEnabledFrom(nextIndex, direction);
        if (alt !== null) nextIndex = alt;
      }

      // if the destination is still disabled or null, abort
      if (timeItems[nextIndex] && timeItems[nextIndex].isPast) return;

        // compute target translate and animate to it for smooth wheel movement
      const newTranslate = computeTranslateForIndex(nextIndex);
      // animate (ease) to the target for smooth wheel movement
      animateTranslateTo(newTranslate, 220);
      setValues(timeOptions[nextIndex]);
      setCurrentIndex(nextIndex);
      // update overlay after DOM paints
      requestAnimationFrame(() => computeHighlight());

      wheelAccumulatorRef.current = 0;
      lastWheelTimeRef.current = now;
    }
  };

  const downScroll = () => {
    if (timeOptions.length === 0) return;
    // if no available times, do nothing
    const firstAvailable = getFirstAvailableIndex();
    if (firstAvailable === null) return;

    // animate via JS helper (animateTranslateTo) to ensure smooth movement and no conflicting CSS transitions

    let next = Math.min(timeOptions.length - 1, currentIndex + 1);
    // if it's disabled, jump to nearest enabled
    if (timeItems[next] && timeItems[next].isPast) {
      const alt = findNearestEnabledFrom(next, 1);
      if (alt !== null) next = alt;
    }
    if (next !== currentIndex) {
      setCurrentIndex(next);
      setValues(timeOptions[next]);
    }
  };

  const upScroll = () => {
    if (timeOptions.length === 0) return;
    const firstAvailable = getFirstAvailableIndex();
    if (firstAvailable === null) return;


    let prev = Math.max(0, currentIndex - 1);
    if (timeItems[prev] && timeItems[prev].isPast) {
      const alt = findNearestEnabledFrom(prev, -1);
      if (alt !== null) prev = alt;
    }
    if (prev !== currentIndex) {
      setCurrentIndex(prev);
      setValues(timeOptions[prev]);
    }
  };

  // Render a single linear list (no circular wrapping) with top/bottom spacers so any item can be centered
  const displayList = [...timeOptions];
  const OFFSET_IN_VIEW = Math.floor(visibleCount / 2); // center selected
  const centerTop = OFFSET_IN_VIEW * itemHeight;

  // translate is now a stateful value computed from the DOM so centers always align
  const [translateYState, setTranslateYState] = useState(0);
  // refs used for JS-based animation to smooth translate changes
  const translateRef = useRef(0);
  const animFrameRef = useRef(null);

  const animateTranslateTo = (target, duration = 320) => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    const start = translateRef.current;
    if (duration === 0) {
      translateRef.current = target;
      setTranslateYState(target);
      return;
    }
    const startTime = performance.now();
    const ease = t => 1 - Math.pow(1 - t, 3); // easeOutCubic
    const step = (now) => {
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / duration);
      const value = start + (target - start) * ease(t);
      translateRef.current = value;
      setTranslateYState(value);
      if (t < 1) {
        animFrameRef.current = requestAnimationFrame(step);
      } else {
        animFrameRef.current = null;
      }
    };
    animFrameRef.current = requestAnimationFrame(step);
  };

  const computeTranslateForIndex = (idx) => {
    const container = listRef.current;
    const inner = listInnerRef.current;
    if (!container || !inner) return 0;
    const child = inner.children[idx + 1]; // account for top spacer
    if (!child) return 0;
    // position child so its top is at centerTop
    const desired = child.offsetTop - (container.clientHeight/2 - child.offsetHeight/2);
    const max = Math.max(0, inner.scrollHeight - container.clientHeight);
    return Math.max(0, Math.min(desired, max));
  };

  // update translate when currentIndex or layout changes
  useEffect(() => {
    if (timeOptions.length === 0) {
      // no animation on empty state
      animateTranslateTo(0, 0);
      return;
    }
    const newTranslate = computeTranslateForIndex(currentIndex);
    // animate to the new position with ease for smooth movement
    animateTranslateTo(newTranslate, 320);
    requestAnimationFrame(() => computeHighlight());
  }, [currentIndex, timeOptions.length, itemHeight, visibleCount]);

  // cleanup any running animation frame on unmount
  useEffect(() => {
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, []);

  // highlight rect state
  const [highlightStyle, setHighlightStyle] = useState(null);

  // compute highlight as a stable function so it can be called on-demand
  const computeHighlight = () => {
    const container = listRef.current;
    if (!container) return;
    if (timeOptions.length === 0) {
      setHighlightStyle(null);
      return;
    }

    // Keep the overlay fixed to the vertical center to avoid measuring while animating
    const contRect = container.getBoundingClientRect();
    const left = 0; // span full width
    const width = contRect.width;
    const top = Math.floor((visibleCount / 2) * itemHeight);
    const height = itemHeight;
    setHighlightStyle({ left, width, top, height });
  };

  // Recompute highlight rect whenever index, sizes, or displayList changes
  useEffect(() => {
    computeHighlight();
    const ro = new ResizeObserver(computeHighlight);
    if (listRef.current) ro.observe(listRef.current);
    if (listInnerRef.current) ro.observe(listInnerRef.current);

    window.addEventListener('resize', computeHighlight);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', computeHighlight);
    }
  }, [currentIndex, itemHeight, visibleCount, displayList.join(','), timeOptions.length]);

  return (
    <div className="timer"  style={{'--primary-color': primaryColor, '--primary-light': primaryColorLight, '--item-height': `${itemHeight}px`, '--visible-count': visibleCount}}>
        <div className="up-icon" >
          <button className="button" onClick={upScroll}><BsChevronUp />
          </button>
        </div>
        <div className="list" ref={listRef} onWheel={handleScroll} style={{ height: `${itemHeight * visibleCount}px`, position: 'relative' }}>
          <div className="center-highlight" ref={centerRef} style={{ position: 'absolute', pointerEvents: 'none', borderRadius: '8px', transition: 'all 140ms ease', zIndex: 2, ...(highlightStyle ? { left: `${highlightStyle.left}px`, width: `${highlightStyle.width}px`, top: `${highlightStyle.top}px`, height: `${highlightStyle.height}px` } : { left: 0, right: 0, top: `${centerTop}px`, height: `${itemHeight}px` }) }} />
          <div className="list-inner" ref={listInnerRef} style={{ transform: `translateY(-${translateYState}px)`, position: 'relative', zIndex: 1 }}>
            <div style={{height: `${OFFSET_IN_VIEW * itemHeight}px`}} />
            {displayList.map((time, idx) => {
              const isPast = timeItems[idx] && timeItems[idx].isPast;
              const isSelected = (idx === currentIndex);
              // always apply the item-level "selected" class so the selected item shows a solid highlight
              const baseClass = isSelected ? 'selected time-list' : 'time-list'
              const bg = isSelected ? selectedColor : ''

              return (
                <p key={`${idx}_${time}`} onClick={() => !isPast && handleTimeSelection(time)} data-disabled={isPast ? 'true' : 'false'} aria-disabled={isPast} className={baseClass} style={{ height: `${itemHeight}px`, lineHeight: `${itemHeight}px`, backgroundColor: bg, whiteSpace: 'nowrap', opacity: isPast ? 0.45 : 1, cursor: isPast ? 'not-allowed' : 'pointer' }}>{time}</p>
              )
            })}
            <div style={{height: `${OFFSET_IN_VIEW * itemHeight}px`}} />
            {timeOptions.length === 0 && <div style={{textAlign: 'center', paddingTop: '0.65rem', color: '#888'}}>No available times</div>}
          </div>
        </div>
        <div className="down-icon">
          <button className="button" onClick={downScroll}><BsChevronDown />
          </button>
        </div>
      

    </div>
  );
};

export default Timer;

